import { useRoutes, Navigate, Outlet } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { Suspense, useMemo, useEffect } from 'react'

import { useStores } from '../providers/withStore'
import { usePrevLocation } from '../providers/PrevLocationProvider'
import ErrorBoundary from '../providers/ErrorBoundary'
import Loader from '../../shared/ui/Loader'
import MainLayout from '../../layouts/MainLayout/MainLayout'

import ProductTour from '../../features/organization/map-picker/ui/ProductTour'
// Импортируем все наши конфигурации роутов
import { getAuthRoutes } from './auth.routes'
import { getOnboardingRoutes } from './onboarding.routes'
import { getOwnerRoutes } from './owner.routes' // Теперь возвращает только дочерние роуты
import { getStaffRoutes } from './staff.routes'

/**
 * Компонент, который выбирает набор роутов для АВТОРИЗОВАННОГО пользователя.
 */
const AppRoutes = observer(() => {
  const { organizationStore, userStore, tourStore } = useStores()
  const { prevLocation } = usePrevLocation()

  // Автоматический запуск тура для новых пользователей
  useEffect(() => {
    // Убедимся, что пользователь загружен и у него есть организация
    if (userStore.user && organizationStore.organization.id) {
      // Проверяем настройку пользователя или флаг принудительного показа.
      // `user.settings` может отсутствовать у старых пользователей, поэтому проверяем на `!== false`.
      if (
        tourStore.forceShowTour ||
        userStore.user.settings?.showTourOnLogin !== false
      ) {
        // Запускаем тур с небольшой задержкой, чтобы UI успел отрисоваться.
        // Также останавливаем предыдущий тур, если он был активен.
        setTimeout(() => {
          tourStore.loadAndStartTour('main') // Запускаем основной тур
        }, 1500)
      }
    }
  }, [
    userStore.user,
    organizationStore.organization.id,
    tourStore,
    tourStore.forceShowTour,
  ])

  const routes = useMemo(() => {
    // Если у пользователя еще нет организации, показываем онбординг
    if (!organizationStore.organization.id) {
      return getOnboardingRoutes()
    }

    const userRoles = userStore.user?.roles || []

    // Если пользователь — владелец
    if (userRoles.includes('owner')) {
      return [
        {
          element: (
            <MainLayout>
              <Suspense fallback={<Loader />}>
                <Outlet />
              </Suspense>
              <ProductTour />
            </MainLayout>
          ),
          children: getOwnerRoutes(),
        },
        {
          path: '*',
          element:
            prevLocation === '/register' ? (
              <Navigate to="/account" replace />
            ) : (
              <Navigate to="/dashboard" replace />
            ),
        },
      ]
    }

    // Если пользователь — сотрудник (но не владелец)
    if (userRoles.includes('staff')) {
      return [
        {
          element: (
            <MainLayout>
              <Suspense fallback={<Loader />}>
                <Outlet />
              </Suspense>
              <ProductTour />
            </MainLayout>
          ),
          children: getStaffRoutes(),
        },
        // Перенаправляем со всех неизвестных путей на календарь сотрудника
        { path: '*', element: <Navigate to="/staff/my-calendar" replace /> },
      ]
    }

    // Если у пользователя есть организация, но нет подходящей роли (например, клиент),
    // или произошла ошибка — перенаправляем на страницу входа.
    // В будущем здесь может быть логика для роли "клиент".
    return [{ path: '*', element: <Navigate to="/login" replace /> }]
  }, [organizationStore.organization.id, userStore.user, prevLocation])

  return useRoutes(routes)
})

/**
 * Главный компонент роутера.
 * Определяет, авторизован ли пользователь, и на основе этого
 * показывает либо публичные страницы, либо защищенные страницы через AppRoutes.
 */
const Router = observer(() => {
  const { authStore, userStore, organizationStore } = useStores()
  const authRoutes = useRoutes(getAuthRoutes())
  // Показываем глобальный лоадер, пока не завершилась проверка аутентификации
  // или пока грузятся данные пользователя/организации после входа.
  if (
    !authStore.isAuthChecked ||
    (authStore.currentUser &&
      (userStore.isLoading || organizationStore.isLoading))
  ) {
    return <Loader fullScreen />
  }

  // Если пользователь авторизован и организация загружена, передаем управление AppRoutes
  if (authStore.currentUser) {
    return <AppRoutes />
  }

  // Если не авторизован — показываем публичные роуты
  return authRoutes
})

const AppRouter = () => (
  <ErrorBoundary>
    <Router />
  </ErrorBoundary>
)

export default AppRouter
